import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useApp } from '../context/AppContext';
import { useNavigate } from 'react-router-dom';
import { Plus, FileText, Search, Tag, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const TechnicalAnalyses = () => {
  const { selectedProject } = useApp();
  const navigate = useNavigate();
  const [analyses, setAnalyses] = useState([]);
  const [filteredAnalyses, setFilteredAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [allTags, setAllTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [analysisToDelete, setAnalysisToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (selectedProject) {
      fetchAnalyses();
    }
  }, [selectedProject]);

  useEffect(() => {
    filterAnalyses();
  }, [analyses, searchTerm, selectedTags]);

  const fetchAnalyses = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/technical-analyses?project_id=${selectedProject.id}`);
      setAnalyses(response.data);
      
      // Extract all unique tags
      const tags = new Set();
      response.data.forEach(analysis => {
        if (analysis.tags && Array.isArray(analysis.tags)) {
          analysis.tags.forEach(tag => tags.add(tag));
        }
      });
      setAllTags(Array.from(tags));
    } catch (error) {
      toast.error('Erro ao carregar análises');
    } finally {
      setLoading(false);
    }
  };

  const filterAnalyses = () => {
    let filtered = [...analyses];

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(analysis => {
        const titleMatch = analysis.title.toLowerCase().includes(searchTerm.toLowerCase());
        const tagMatch = analysis.tags && analysis.tags.some(tag => 
          tag.toLowerCase().includes(searchTerm.toLowerCase())
        );
        return titleMatch || tagMatch;
      });
    }

    // Filter by selected tags
    if (selectedTags.length > 0) {
      filtered = filtered.filter(analysis => {
        if (!analysis.tags) return false;
        return selectedTags.every(selectedTag => analysis.tags.includes(selectedTag));
      });
    }

    setFilteredAnalyses(filtered);
  };

  const toggleTag = (tag) => {
    setSelectedTags(prev => 
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  const handleDeleteClick = (analysis) => {
    setAnalysisToDelete(analysis);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!analysisToDelete) return;
    
    setDeleting(true);
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_URL}/api/technical-analyses/${analysisToDelete.id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // Remove from local state
      setAnalyses(prev => prev.filter(a => a.id !== analysisToDelete.id));
      
      toast.success('Análise técnica excluída com sucesso');
      setDeleteDialogOpen(false);
      setAnalysisToDelete(null);
    } catch (error) {
      console.error('Error deleting analysis:', error);
      toast.error('Não foi possível excluir a análise técnica. Tente novamente.');
    } finally {
      setDeleting(false);
    }
  };


  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!selectedProject) {
    return <div className="text-center py-12" >Selecione um projeto</div>;
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Análises Técnicas</h1>
          <p className="text-sm text-muted-foreground mt-1">Documentação técnica e análises detalhadas de UX</p>
        </div>
        <Button onClick={() => navigate('/analyses/new')} data-testid="create-analysis-button">
          <Plus className="mr-2 h-4 w-4" />
          Nova Análise Técnica
        </Button>
      </div>

      {/* Search and Filters */}
      <Card className="p-4 bg-card border-border">
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Search className="h-5 w-5 text-muted-foreground" />
            <Input
              placeholder="Buscar por título ou tag..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1"
              data-testid="search-input"
            />
          </div>

          {allTags.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Tag className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium text-foreground">Filtrar por tags:</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {allTags.map(tag => (
                  <Badge
                    key={tag}
                    variant={selectedTags.includes(tag) ? "default" : "outline"}
                    className="cursor-pointer"
                    onClick={() => toggleTag(tag)}
                    data-testid={`tag-filter-${tag}`}
                  >
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Results Table */}
      <Card className="bg-card border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-muted-foreground font-semibold">Título</TableHead>
              <TableHead className="text-muted-foreground font-semibold">Projeto</TableHead>
              <TableHead className="text-muted-foreground font-semibold">Autor</TableHead>
              <TableHead className="text-muted-foreground font-semibold">Data de Atualização</TableHead>
              <TableHead className="text-muted-foreground font-semibold">Tags</TableHead>
              <TableHead className="text-muted-foreground font-semibold text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredAnalyses.length > 0 ? (
              filteredAnalyses.map(analysis => (
                <TableRow
                  key={analysis.id}
                  className="cursor-pointer hover:bg-accent"
                  onClick={() => navigate(`/analyses/${analysis.id}`)}
                  data-testid={`analysis-row-${analysis.id}`}
                >
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4"  />
                      <span className="font-medium" >{analysis.title}</span>
                      {analysis.published_to_portal && (
                        <Badge className="bg-indigo-100 text-indigo-700 border-indigo-300 text-xs" data-testid={`portal-badge-${analysis.id}`}>No portal</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell >{selectedProject.name}</TableCell>
                  <TableCell >{analysis.author_name}</TableCell>
                  <TableCell >{formatDate(analysis.updated_at)}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {analysis.tags && analysis.tags.map(tag => (
                        <Badge key={tag} variant="secondary" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(analysis);
                      }}
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      data-testid={`delete-analysis-${analysis.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-12">
                  <div >
                    <FileText size={48} className="mx-auto mb-4 opacity-50" />
                    <p className="mb-2">
                      {searchTerm || selectedTags.length > 0 
                        ? 'Nenhuma análise encontrada com os filtros aplicados'
                        : 'Nenhuma análise técnica encontrada'
                      }
                    </p>
                    {!searchTerm && selectedTags.length === 0 && (
                      <Button onClick={() => navigate('/analyses/new')} variant="outline" className="mt-4">
                        Criar primeira análise
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir análise técnica</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir "{analysisToDelete?.title}"? Esta ação não poderá ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? 'Excluindo...' : 'Excluir'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
